import { useContext, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { useApPad } from '../../ui/use-ap-pad';
import { uiText } from '../../i18n';
import { StudioIcon } from './StudioIcon';
import { StudioSheet } from './StudioSheet';
import { SettingsDrillContext } from './settings-drill-context';
import { OffButton } from '../../ui/OffButton';
import { uiFormat } from '../../i18n';
import { reduced, shake } from '../../ui/motion';
import { platform } from '../../platform';
import { StudioPoorSheet, type StudioCheapOffer } from './StudioPoorSheet';
import { ModeReplacedContext } from './use-held-mode';
import { studioSettingCells, type Cell } from './studio-settings-model';

/** Ячейка карточки «Ещё настройки» (APP-SPEC-5 §1.5, BotCore.settingsCard).
    Все поля, кроме title и value, необязательны: прежние экраны передают только их. */
export interface StudioSettingSlot {
  title: string;
  value: string;
  disabled?: boolean;
  /** Секция листа, к которой прокручивается лист (атрибут data-sc-sec у StudioSettingsSection) */
  section?: string;
  /** Вид ячейки: info — сведения или замок (лист не открывается, нажатие — дрожь и подсказка), files — своё действие экрана */
  kind?: 'choice' | 'text' | 'info' | 'files' | 'toggle' | 'cell';
  /** Ячейка-переключатель (kind: 'toggle'): состояние и смена прямо в ячейке, лист не открывается */
  on?: boolean;
  onToggle?(next: boolean): void;
  /** Пустая: пунктир с «＋» и подпись «Добавить» (текст без значения, файлов нет) */
  empty?: boolean;
  /** Подпись пустой ячейки вместо «Добавить» */
  add?: string;
  /** Ячейка во всю ширину сетки (длинное значение) */
  wide?: boolean;
  /** Подсказка ячейки info по нажатию */
  tip?: string;
  /** Закреплённая ячейка: замок вместо «i» */
  lock?: boolean;
  /** Слой листа, который открывается сразу (строка блока «Съёмка»: `shoot:camera`) */
  drill?: string;
  /** Своя миниатюра 36 px */
  thumb?: ReactNode;
  /** Своё действие ячейки files (лист не открывается) */
  onTap?(): void;
  /** Ячейка блока (`shoot`): правило B1 сворачивает лишние ячейки блока в «+N» */
  group?: string;
}
function SettingPreview({ slot }: { slot: StudioSettingSlot }) {
  if (slot.thumb) return <>{slot.thumb}</>;
  if (slot.title === 'Формат') {
    const ratios: Record<string, string> = {
      Квадрат: '1:1',
      Портрет: '3:4',
      Пост: '4:5',
      Сторис: '9:16',
      Широкий: '16:9',
      Горизонтальное: '16:9',
      'Печать 10 × 15': '2:3',
    };
    const ratio = (ratios[slot.value] ?? slot.value).match(/^(\d+):(\d+)$/);
    const aspect = ratio ? Number(ratio[1]) / Number(ratio[2]) : 1;
    const width = Math.min(24, 24 * aspect),
      height = Math.min(24, 24 / aspect);
    return (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
        {slot.value === 'Кружок' ? (
          <circle cx="16" cy="16" r="11" />
        ) : (
          <rect x={(32 - width) / 2} y={(32 - height) / 2} width={width} height={height} rx="3" />
        )}
      </svg>
    );
  }
  if (slot.title === 'Количество' || slot.title === 'Сколько фото')
    return <span className="mono">{slot.value.match(/\d+/)?.[0] ?? slot.value}</span>;
  const kind =
    slot.title === 'Голос' || slot.title === 'Звук'
      ? slot.title === 'Звук'
        ? 'wave'
        : 'voice'
      : slot.title === 'Длительность'
        ? 'video'
        : slot.kind === 'text' || /изменить|двигаться|пожелан|подпис/i.test(slot.title)
          ? 'edit'
          : slot.kind === 'files' || slot.title === 'Фон' || slot.title === 'Свой референс'
            ? 'image'
            : slot.group === 'shoot' || slot.title === 'Съёмка'
              ? 'camera'
              : 'sliders';
  return <StudioIcon kind={kind} />;
}
const lockIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const infoIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);
/** Управление листом снаружи: открыть сразу на секции (и слое drill) или закрыть. */
export interface StudioSettingsControl {
  open(section?: string, drill?: string): void;
  close(): void;
}
/** Подпись секции листа «Ещё настройки»: секция с якорем, к которой прокручивается лист при нажатии ячейки. */
export function StudioSettingsSection({
  id,
  title,
  hint,
  kind = 'choice',
  children,
}: {
  id: string;
  title?: string;
  hint?: string;
  kind?: string;
  children: ReactNode;
}) {
  return (
    <section className={`sc-sec sc-${kind}`} data-sc-sec={id}>
      {title && kind !== 'toggle' && (
        <div className="glbl">
          {uiText(title)}
          {hint && <span>{uiText(hint)}</span>}
        </div>
      )}
      {children}
    </section>
  );
}
/** Строка сведений или замка в листе (`info`): нажатие — дрожь и подсказка с причиной. */
export function StudioInfoRow({ title, value, tip, lock, icon }: { title: string; value: string; tip: string; lock?: boolean; icon?: ReactNode }) {
  const [shown, setShown] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!shown) return;
    const timer = window.setTimeout(() => setShown(false), 4000);
    return () => clearTimeout(timer);
  }, [shown]);
  return (
    <div className="studio-info-row">
      <button
        ref={ref}
        type="button"
        className="sc-r studio-info"
        aria-disabled="true"
        aria-label={`${uiText(title)}: ${uiText(value)}`}
        onClick={() => {
          shake(ref.current);
          platform.haptic('light');
          setShown(value => !value);
        }}
      >
        <span className="sc-th o">{icon ?? <StudioIcon kind="wave" />}</span>
        <span className="m">
          <b>{uiText(value)}</b>
          <small>{uiText(title)}</small>
        </span>
        <span className="sc-i">{lock ? lockIcon : infoIcon}</span>
      </button>
      {shown && (
        <p className="studio-settings-tip" role="status">
          {uiText(tip)}
        </p>
      )}
    </div>
  );
}

/** Карточка и лист настроек из шаблона; значения и изменения остаются у API-формы.
    oneRow — одна ячейка без шапки (§1.5: «слотов меньше двух — карточки нет, единственный выбор стоит строкой-слотом»). */
export function StudioSettings({
  children,
  slots,
  disabled,
  oneRow = false,
  control,
}: {
  children: ReactNode;
  slots: StudioSettingSlot[];
  disabled?: boolean;
  oneRow?: boolean;
  /** Открыть лист снаружи: control.current.open('format') */
  control?: Ref<StudioSettingsControl>;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<string | null>(null);
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [tip, setTip] = useState<{ key: string; text: string } | null>(null);
  const [active, setActive] = useState<string[]>([]);
  const body = useRef<HTMLDivElement>(null);
  const before = useRef<Record<string, string>>({});
  const cells = studioSettingCells(slots);
  const one = oneRow && cells.length === 1;
  const keyOf = (slot: StudioSettingSlot, index: number) => (slot.title || `more-${index}`) + (slot.group ? `@${slot.group}` : '');
  const close = () => {
    if (page) setPage(null);
    else finish();
  };
  function finish() {
    setOpen(false);
    setPage(null);
    setTarget(null);
    // «Активация» ячеек (§1.5): сменившиеся после листа ячейки проявляются заново.
    const changed = cells.map(keyOf).filter((key, index) => before.current[key] !== undefined && before.current[key] !== cells[index].value);
    if (changed.length && !reduced()) setActive(changed);
  }
  useEffect(() => {
    if (!active.length) return;
    const timer = window.setTimeout(() => setActive([]), 260 + active.length * 40);
    return () => clearTimeout(timer);
  }, [active]);
  useEffect(() => {
    if (!tip) return;
    const timer = window.setTimeout(() => setTip(null), 4000);
    return () => clearTimeout(timer);
  }, [tip]);
  useLayoutEffect(() => {
    if (!open || !target || page) return;
    const frame = requestAnimationFrame(() => {
      const section = body.current?.querySelector<HTMLElement>(`[data-sc-sec="${CSS.escape(target)}"]`);
      if (!section) return;
      section.scrollIntoView?.({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
      section.classList.remove('hl');
      void section.offsetWidth;
      section.classList.add('hl');
      window.setTimeout(() => section.classList.remove('hl'), 700);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, target, page]);
  const show = (section?: string, drill?: string) => {
    before.current = Object.fromEntries(cells.map((slot, index) => [keyOf(slot, index), slot.value]));
    setTip(null);
    setTarget(section ?? null);
    setPage(drill ?? null);
    setOpen(true);
  };
  useImperativeHandle(control, () => ({ open: (section?: string, drill?: string) => show(section, drill), close: finish }));
  const tap = (slot: Cell, element: HTMLElement) => {
    if (slot.kind === 'toggle') {
      slot.onToggle?.(!slot.on);
      return;
    }
    if (slot.kind === 'info') {
      shake(element);
      platform.haptic('light');
      if (slot.tip) setTip(current => (current?.key === slot.title ? null : { key: slot.title, text: slot.tip! }));
      return;
    }
    if (slot.kind === 'files' && slot.onTap) {
      slot.onTap();
      return;
    }
    show(slot.section, slot.drill);
  };
  return (
    <div className={`bc-sc${one ? ' one' : ''}`}>
      {!one && (
        <button type="button" className="sc-h" disabled={disabled} onClick={() => show()}>
          <span className="hi">
            <StudioIcon kind="sliders" />
          </span>
          <b>{uiText('Ещё настройки')}</b>
          <span className="r">
            {uiText('Изменить')}
            <StudioIcon kind="chev" />
          </span>
        </button>
      )}
      <div className="sc-g" role="group" aria-label={uiText('Ещё настройки')}>
        {cells.map((slot, index) => {
          const key = keyOf(slot, index);
          const wide = slot.wide || (cells.length > 1 && cells.length % 2 === 1 && index === cells.length - 1) || one;
          const value = slot.empty
            ? uiText(slot.add ?? 'Добавить')
            : uiText(slot.kind === 'toggle' && !slot.value ? (slot.on ? 'Вкл' : 'Выкл') : slot.value);
          const info = slot.kind === 'info';
          return (
            <button
              key={key}
              type="button"
              className={`sc-s${slot.empty ? ' e' : ''}${wide ? ' w' : ''}${info ? ' inf' : ''}${one ? ' row' : ''}${slot.more ? ' mr' : ''}${active.includes(key) ? ' studio-sc-act' : ''}`}
              disabled={disabled || slot.disabled}
              aria-disabled={info ? 'true' : undefined}
              role={slot.kind === 'toggle' ? 'switch' : undefined}
              aria-checked={slot.kind === 'toggle' ? !!slot.on : undefined}
              aria-label={slot.more ? `${uiText('Ещё')} ${slot.more.length}: ${slot.more.map(uiText).join(', ')}` : `${uiText(slot.title)}: ${value}`}
              data-sc-cell={slot.section}
              onClick={event => tap(slot, event.currentTarget)}
            >
              <span className={`sc-th${slot.empty ? ' dsh' : ''}`}>
                {slot.more ? (
                  <b className="sc-n">+{slot.more.length}</b>
                ) : slot.empty ? (
                  <StudioIcon kind="plus" />
                ) : (
                  <SettingPreview slot={slot} />
                )}
              </span>
              <span className="m">
                {slot.title && <span className="sc-k">{uiText(slot.title)}</span>}
                <span className="sc-v">{value}</span>
              </span>
              {info && (slot.lock || slot.tip) && <span className="sc-i">{slot.lock ? lockIcon : infoIcon}</span>}
              {slot.kind === 'toggle' && <span className={`sc-sw${slot.on ? ' on' : ''}`} aria-hidden="true" />}
              {one && !info && slot.kind !== 'toggle' && (
                <span className="sc-i">
                  <StudioIcon kind="chev" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {tip && (
        <p className="studio-settings-tip" role="status">
          {uiText(tip.text)}
        </p>
      )}
      {open && (
        <StudioSheet
          title="Ещё настройки"
          onClose={close}
          closeButton={!page}
          footer={
            !page && (
              <button type="button" className="btn" onClick={finish}>
                {uiText('Готово')}
              </button>
            )
          }
        >
          <SettingsDrillContext.Provider value={{ page, host, open: setPage, back: () => setPage(null) }}>
            <div hidden={!!page} ref={body} className="sc-sb">
              <h2>{uiText('Ещё настройки')}</h2>
              <p className="studio-settings-sub">{uiText('Необязательно: всё уже настроено')}</p>
              <div className="studio-native-settings">{children}</div>
            </div>
            <div ref={setHost} hidden={!page} className="studio-settings-drill" />
          </SettingsDrillContext.Provider>
        </StudioSheet>
      )}
    </div>
  );
}
export function StudioFormFoot({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useApPad(ref);
  return (
    <div ref={ref} className="ap-foot studio-native-foot">
      {children}
    </div>
  );
}
/** Общая стабильная CTA медиа-экранов (§1.6): цена с первого открытия, подсказка «чего не хватает»,
    при нехватке Circle — «Не хватает N C · Пополнить» и лист «Не хватает Circle». */
export function StudioCreateButton({
  kind,
  price,
  busy,
  blocked,
  pending,
  missing,
  available,
  onCreate,
  label,
  from = false,
  balance,
  cheap,
  onCheap,
  uploading = false,
}: {
  /** Вид для подписи по умолчанию («Создать фото», «Создать видео»); другие экраны передают label */
  kind: 'photo' | 'video' | (string & {});
  price: number;
  busy: boolean;
  blocked: boolean;
  pending: boolean;
  missing: string | null;
  available: boolean;
  onCreate(): void;
  /** Своя подпись без цены («Создать 2 фото»); цена дописывается сама */
  label?: string;
  /** Итог зависит от длины файла и файла ещё нет: «от N C» */
  from?: boolean;
  /** Остаток Circle (balance.circle из GET /app/me); неизвестен — лист не предлагается, сервер проверит сам */
  balance?: number | null;
  /** Скидка «Сделать дешевле», если экран её публикует; null — «Для этой работы скидки нет» */
  cheap?: StudioCheapOffer | null;
  onCheap?(): void;
  /** Идёт загрузка файла, а не отправка заказа: кнопка стоит как есть (без приглушения), нажатие говорит «Загружаем файл»;
      заказ до конца загрузки не уйдёт (use-studio-form: submit ждёт uploading) */
  uploading?: boolean;
}) {
  const [poor, setPoor] = useState(false);
  /* тело формы сменило прежнее на том же экране (смена режима): кнопка уже стоит, второй раз она не въезжает */
  const replaced = useContext(ModeReplacedContext);
  const short = typeof balance === 'number' && Number.isFinite(price) && price > 0 && balance < price && !from;
  const title = uiText(label ?? (kind === 'photo' ? 'Создать фото' : 'Создать видео'));
  const priced = from ? uiFormat('{0} · от {1} C', title, price) : uiFormat('{0} · {1} C', title, price);
  const ready = !busy && !blocked && !pending && !missing && available;
  return (
    <StudioFormFoot>
      <OffButton
        className={replaced ? 'photo-submit' : 'fin photo-submit'}
        disabled={busy && !uploading}
        label={
          busy && !uploading
            ? uiText('Отправляем…')
            : pending
              ? uiText('Продолжить проверку')
              : ready && short
                ? uiFormat('Не хватает {0} C · Пополнить', price - (balance as number))
                : priced
        }
        miss={
          uploading
            ? uiText('Загружаем файл')
            : blocked
            ? uiText('Проверяем доступность')
            : pending
              ? undefined
              : missing || (!available ? uiText('Создание сейчас недоступно') : undefined)
        }
        onClick={() => {
          if (busy || blocked) return;
          if (pending) return onCreate();
          if (missing || !available) return;
          if (short) setPoor(true);
          else onCreate();
        }}
      />
      {poor && typeof balance === 'number' && (
        <StudioPoorSheet
          price={price}
          balance={balance}
          cheap={cheap ?? null}
          onClose={() => setPoor(false)}
          onCheap={() => {
            setPoor(false);
            onCheap?.();
          }}
        />
      )}
    </StudioFormFoot>
  );
}
